import { Link } from 'react-router-dom'
import { Package, Users, Eye, Download, TrendingUp, Clock, ArrowRight } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { modelsApi } from '@/services/api/models'
import { leadsApi } from '@/services/api/leads'
import { useAuth } from '@/hooks/useAuth'
import { format } from 'date-fns'

export default function Dashboard() {
  const { token } = useAuth()

  // Fetch models stats
  const { data: modelsData } = useQuery({
    queryKey: ['admin-models-stats'],
    queryFn: () => modelsApi.getModels({ limit: 5, sort: 'newest' }, token!)
  })

  // Fetch leads stats
  const { data: leadsData } = useQuery({
    queryKey: ['admin-leads-stats'],
    queryFn: () => leadsApi.getLeads({ limit: 5 })
  })

  const totalModels = modelsData?.pagination?.total || 0
  const totalLeads = leadsData?.pagination?.total || 0
  const newLeads = leadsData?.data?.filter(lead => lead.status === 'new').length || 0
  const featuredModels = modelsData?.data?.filter(model => model.featured).length || 0

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-gray-600">Welcome back! Here's what's happening with your platform.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Models</p>
              <p className="text-3xl font-semibold text-gray-900 mt-1">{totalModels}</p>
            </div>
            <div className="p-3 bg-indigo-100 rounded-lg">
              <Package className="w-6 h-6 text-indigo-600" />
            </div>
          </div>
          <Link 
            to="/admin/models" 
            className="mt-4 flex items-center text-sm text-indigo-600 hover:text-indigo-800"
          >
            Manage models
            <ArrowRight className="ml-1 w-4 h-4" />
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Leads</p>
              <p className="text-3xl font-semibold text-gray-900 mt-1">{totalLeads}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Users className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <Link 
            to="/admin/leads" 
            className="mt-4 flex items-center text-sm text-green-600 hover:text-green-800"
          >
            View leads
            <ArrowRight className="ml-1 w-4 h-4" />
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">New Leads</p>
              <p className="text-3xl font-semibold text-gray-900 mt-1">{newLeads}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-500">Require attention</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Featured Models</p>
              <p className="text-3xl font-semibold text-gray-900 mt-1">{featuredModels}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Eye className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-500">Currently showcased</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Models */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Recent Models</h2>
              <Link 
                to="/admin/models" 
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                View all
              </Link>
            </div>
          </div>
          <div className="divide-y">
            {modelsData?.data?.slice(0, 5).map((model) => (
              <div key={model.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {model.thumbnailPath ? (
                      <img 
                        src={modelsApi.getThumbnailUrl(model.id, '256')} 
                        alt={model.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                        <Package className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{model.name}</p>
                      <p className="text-sm text-gray-500">{model.category}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Eye className="w-4 h-4" />
                      {model.viewCount}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Download className="w-4 h-4" />
                      {model.downloadCount}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {(!modelsData?.data || modelsData.data.length === 0) && (
              <div className="p-8 text-center text-gray-500">
                No models yet. <Link to="/admin/models/new" className="text-indigo-600">Add your first model</Link>
              </div>
            )}
          </div>
        </div>

        {/* Recent Leads */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Recent Leads</h2>
              <Link 
                to="/admin/leads" 
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                View all
              </Link>
            </div>
          </div>
          <div className="divide-y">
            {leadsData?.data?.slice(0, 5).map((lead) => (
              <div key={lead.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{lead.name}</p>
                    <p className="text-sm text-gray-500">{lead.email}</p>
                    <p className="text-xs text-gray-400 mt-1">{lead.projectType?.replace(/_/g, ' ') || 'Not specified'}</p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full
                      ${lead.status === 'new' ? 'bg-blue-100 text-blue-800' : ''}
                      ${lead.status === 'contacted' ? 'bg-yellow-100 text-yellow-800' : ''}
                      ${lead.status === 'qualified' ? 'bg-purple-100 text-purple-800' : ''}
                      ${lead.status === 'won' ? 'bg-green-100 text-green-800' : ''}
                      ${lead.status === 'lost' ? 'bg-red-100 text-red-800' : ''}
                    `}>
                      {lead.status}
                    </span>
                    <p className="text-xs text-gray-400 mt-1">
                      {lead.createdAt ? format(new Date(lead.createdAt), 'MMM d') : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            {(!leadsData?.data || leadsData.data.length === 0) && (
              <div className="p-8 text-center text-gray-500">
                No leads yet. They will appear here when submitted through the contact form.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/admin/models/new"
            className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
          >
            <Package className="w-5 h-5 text-gray-600" />
            <span className="font-medium text-gray-700">Add New Model</span>
          </Link>
          <Link
            to="/admin/leads?status=new"
            className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors"
          >
            <Users className="w-5 h-5 text-gray-600" />
            <span className="font-medium text-gray-700">View New Leads</span>
          </Link>
          <Link
            to="/admin/models?featured=true"
            className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors"
          >
            <Eye className="w-5 h-5 text-gray-600" />
            <span className="font-medium text-gray-700">Featured Models</span>
          </Link>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-500 hover:bg-gray-50 transition-colors"
          >
            <Clock className="w-5 h-5 text-gray-600" />
            <span className="font-medium text-gray-700">View Live Site</span>
          </a>
        </div>
      </div>
    </div>
  )
}